import { axeSweepGeometry, diskTouchesAnnularSector, sweptBladeHalfAngleRad } from './axe-sweep-geometry.js';
/**
 * Canonical WEAPON_CONTACT geometry.
 *
 * This module is the spatial owner for skills that borrow an equipped
 * weapon's contact profile.  It deliberately reuses the ratified basic
 * attack geometry values; source damage and cadence remain owned by
 * source-profile.ts.  The result of a contact includes the stable region and
 * its already-ratified damage fraction so callers can apply the ordinary
 * damage pipeline without manufacturing a second damage formula.
 */

import type { CanonicalGeometryV2, SkillApplicationV2 } from '../skill-engine/types.js';
import { WEAPON_PROGRESSION_V0, type BalanceWeaponId, type PricingWeaponId } from '../reference/weapon-progression-v0.js';
import type { DamageEffect, WeaponFamily } from '../types.js';
import {
  BALANCE_ASSASSIN_BLADE_THRUST_WIDTH_M,
  BALANCE_CATALYST_CONTACT_V0,
  BALANCE_GREATSWORD_THRUST_WIDTH_M,
  BALANCE_SPECIAL_GEOMETRY_V0,
  BALANCE_THRUST_GEOMETRY_V0,
  BALANCE_WEAPON_GEOMETRY_V0,
  resolveBalanceAttack,
  type BalanceSpatialProfile,
  type WeaponContactProfileId,
} from './basic-attack-geometry.js';
import { resolveEnemyHitboxRadiusM } from './archetypes.js';
import type { CombatSourceDefinition } from './source-profile.js';
import type { Enemy, SimState } from './state.js';
import { distance, lineOfEffect, type Vec2 } from '../terrain.js';

export interface WeaponContactHit {
  readonly profileId: WeaponContactProfileId;
  readonly regionId: string;
  readonly role: 'PRIMARY' | 'SECONDARY';
  /** Fraction of the source hit represented by this contact region. */
  readonly fraction: number;
}

/** Sum of the runtime-resolved contact regions; an unannotated contact has full magnitude. */
export function weaponContactMagnitude(regions: readonly WeaponContactHit[] | undefined): number {
  return regions?.reduce((sum, hit) => sum + hit.fraction, 0) ?? 1;
}

export interface WeaponContactResolvedApplication {
  readonly profileId: WeaponContactProfileId;
  readonly reachM: number;
  readonly angleDeg: number;
  readonly widthM: number;
  /** Present when this contact is a thrust: the weapon's corridor, not its sweep sector. */
  readonly thrust?: true;
  /**
   * A thrust that turns into a sector sweep when the target is nearer than
   * `withinM` (the spear's close sweep): the sector it then covers.
   */
  readonly closeSweep?: { readonly reachM: number; readonly angleDeg: number; readonly withinM: number };
  readonly axeSweep?: ReturnType<typeof axeSweepGeometry>;
  /**
   * A swing whose width scale is not 1: the half-angle, in radians, its blade
   * adds past each end of the sweep — `sweptBladeHalfAngleRad` of the width
   * beyond the weapon's own. The axe is the one swing that states its blade in
   * `axeSweep` instead, where the same rule is already in the model.
   */
  readonly widthExtensionRad?: number;
  readonly rootRegionId?: string;
  readonly application: SkillApplicationV2;
}

/** The immutable identity subset needed at release; SourceSnapshot satisfies it. */
export type WeaponContactSourceIdentity = Pick<CombatSourceDefinition, 'weaponId' | 'family'>;

export interface WeaponContactValidationFailure {
  readonly ok: false;
  readonly code: string;
  readonly message: string;
  readonly path: string;
}

export interface WeaponContactValidationSuccess {
  readonly ok: true;
  readonly value: WeaponContactResolvedApplication;
}

export type WeaponContactValidationResult = WeaponContactValidationSuccess | WeaponContactValidationFailure;

export interface WeaponContactEvaluationGeometry {
  readonly profileId: WeaponContactProfileId;
  /** Expected contact area in square metres before enemy density is applied. */
  readonly areaM2: number;
  /**
   * Thrust contacts only: the corridor, already dilated by the enemy radius.
   * A thrust is aimed at the nearest enemy and runs the full reach, so it is
   * priced like an aimed projectile — extra bodies only behind the target —
   * rather than as a flat area from the wielder outward.
   */
  readonly thrust?: { readonly reachM: number; readonly halfWidthM: number };
  /** Guaranteed primary contact is the aimed weapon region. */
  readonly expectedPrimaryRegionMultiplier: number;
  /** Region-weighted multiplier for each additional expected target. */
  readonly expectedAdditionalRegionMultiplier: number;
}

/**
 * Convert primary/extra contact projections to the scalar expected by the
 * existing leaf pricing function. The primary target is guaranteed by the
 * delivery model and keeps its full primary-region magnitude; only expected
 * additional targets use the area-weighted region mix.
 */
export function expectedContactMagnitudeMultiplier(
  geometry: Pick<WeaponContactEvaluationGeometry, 'expectedPrimaryRegionMultiplier' | 'expectedAdditionalRegionMultiplier'>,
  expectedTargets: number,
): number {
  const targets = Math.max(1, expectedTargets);
  return (geometry.expectedPrimaryRegionMultiplier
    + Math.max(0, targets - 1) * geometry.expectedAdditionalRegionMultiplier) / targets;
}

/**
 * Whether a damage or dot leaf deals its weapon contact's region magnitudes. A leaf without the policy deals its full
 * amount on every contact, whatever region touched the target. The runtime, the pricing walk and every count of the hits
 * a weapon contact's damage is priced over read this one rule.
 */
export function inheritsWeaponRegionMagnitude(leaf: { readonly contactMagnitudePolicy?: DamageEffect['contactMagnitudePolicy'] }): boolean {
  return leaf.contactMagnitudePolicy === 'INHERIT_WEAPON_REGION';
}

interface RegionMagnitudeNode {
  readonly kind: string;
  readonly contactMagnitudePolicy?: DamageEffect['contactMagnitudePolicy'];
  readonly children?: readonly RegionMagnitudeNode[];
}

/**
 * Whether the hits an attack's effects are priced over carry its weapon contact's region magnitudes: its damage and dot
 * leaves, those under a condition or a trigger included and a secondary attack's own excluded, by
 * `inheritsWeaponRegionMagnitude`. Effects with no such leaf keep the region magnitudes. `undefined` when the leaves
 * disagree, since one hit count per attack cannot price both.
 */
export function effectsInheritWeaponRegionMagnitude(effects: readonly RegionMagnitudeNode[]): boolean | undefined {
  const leaves: RegionMagnitudeNode[] = [];
  const visit = (nodes: readonly RegionMagnitudeNode[]): void => {
    for (const node of nodes) {
      if (node.kind === 'damage' || node.kind === 'dot') leaves.push(node);
      else if (node.kind === 'condition' || node.kind === 'trigger') visit(node.children ?? []);
    }
  };
  visit(effects);
  const inherits = leaves.map(inheritsWeaponRegionMagnitude);
  if (inherits.every(Boolean)) return true;
  return inherits.some(Boolean) ? undefined : false;
}

type WeaponGeometry = Extract<CanonicalGeometryV2, { readonly owner: 'WEAPON_CONTACT' }>;

const PROFILE_ALIASES: Readonly<Record<string, WeaponContactProfileId>> = Object.freeze({
  DAGGER: 'DAGGER_PAIR', DAGGER_PAIR: 'DAGGER_PAIR',
  SWORD: 'SWORD',
  AXE: 'HEAVY_AXE', HEAVY_AXE: 'HEAVY_AXE',
  SPEAR: 'SPEAR', POLEARM: 'SPEAR',
  BOW: 'BOW_REFERENCE', BOW_REFERENCE: 'BOW_REFERENCE',
  ASSASSIN_BLADE: 'ASSASSIN_BLADE',
  GREATSWORD: 'GREATSWORD',
  // All three catalysts borrow the one catalyst contact shape.
  CATALYST: 'CATALYST', CATALYST_REFERENCE: 'CATALYST', ARCANE_CATALYST: 'CATALYST', BLAST_CATALYST: 'CATALYST', RAPID_CATALYST: 'CATALYST',
});

const REGION_ALIASES: Readonly<Record<WeaponContactProfileId, Readonly<Record<string, string>>>> = Object.freeze({
  DAGGER_PAIR: Object.freeze({ BLADE: 'blade', PRIMARY: 'blade' }),
  SWORD: Object.freeze({ EDGE: 'edge', BLADE: 'edge', PRIMARY: 'edge' }),
  HEAVY_AXE: Object.freeze({ HEAD: 'head', PRIMARY: 'head', SHAFT: 'shaft', SECONDARY: 'shaft' }),
  SPEAR: Object.freeze({ TIP: 'tip', PRIMARY: 'tip', SHAFT: 'shaft', SECONDARY: 'shaft' }),
  BOW_REFERENCE: Object.freeze({ ARROW: 'arrow', PRIMARY: 'arrow' }),
  ASSASSIN_BLADE: Object.freeze({ BLADE: 'blade', PRIMARY: 'blade' }),
  GREATSWORD: Object.freeze({ BLADE: 'blade', EDGE: 'blade', PRIMARY: 'blade' }),
  CATALYST: Object.freeze({ BLADE: 'blade', EDGE: 'blade', PRIMARY: 'blade' }),
});

function token(value: string): string {
  return value.trim().replace(/[-\s]/g, '_').toUpperCase();
}

export function normalizeWeaponContactProfileId(value: string): WeaponContactProfileId | undefined {
  return PROFILE_ALIASES[token(value)];
}

export function normalizeWeaponContactRegionId(profileId: WeaponContactProfileId, value: string): string | undefined {
  const aliases = REGION_ALIASES[profileId];
  return aliases[token(value)] ?? (Object.values(aliases).includes(value) ? value : undefined);
}

export function weaponContactRegionIds(profileId: WeaponContactProfileId): readonly string[] {
  return [...new Set(Object.values(REGION_ALIASES[profileId]))];
}

/** The family an equipped source must belong to for this contact profile. */
export function weaponContactProfileFamily(profileId: WeaponContactProfileId): WeaponFamily {
  return profileId === 'CATALYST' ? 'CATALYST' : WEAPON_PROGRESSION_V0[profileId].family;
}

/** What a craft on this contact profile is priced against: the catalysts share the catalyst reference. */
export function weaponContactPricingBasis(profileId: WeaponContactProfileId): PricingWeaponId {
  return profileId === 'CATALYST' ? 'CATALYST_REFERENCE' : profileId;
}

type SectorContactProfile = BalanceSpatialProfile | typeof BALANCE_CATALYST_CONTACT_V0;

function weaponProfile(profileId: WeaponContactProfileId): SectorContactProfile | undefined {
  // BOW_REFERENCE is a projectile reference, not an instantaneous melee
  // contact profile.  Keeping it in the closed id vocabulary is useful for
  // source identity validation, but it must never be lowered to a cone.
  if (profileId === 'BOW_REFERENCE') return undefined;
  if (profileId === 'ASSASSIN_BLADE' || profileId === 'GREATSWORD') return undefined;
  if (profileId === 'CATALYST') return BALANCE_CATALYST_CONTACT_V0;
  return BALANCE_WEAPON_GEOMETRY_V0[profileId as keyof typeof BALANCE_WEAPON_GEOMETRY_V0];
}

/**
 * The weapon's own blade width: its shape entry, or — for the two weapons with
 * none — the provisional width their thrust corridor already uses.
 */
function bladeWidthM(profileId: WeaponContactProfileId): number {
  const profile = weaponProfile(profileId);
  if (profile !== undefined && 'shape' in profile) return profile.shape.primaryWidth;
  return profileId === 'GREATSWORD' ? BALANCE_GREATSWORD_THRUST_WIDTH_M : profileId === 'ASSASSIN_BLADE' ? BALANCE_ASSASSIN_BLADE_THRUST_WIDTH_M : 0;
}

/** The outer length of the reach the blade occupies; a weapon with no shape entry is blade end to end. */
function bladeLengthM(profileId: WeaponContactProfileId, reachM: number): number {
  const profile = weaponProfile(profileId);
  return profile !== undefined && 'shape' in profile ? profile.shape.primaryDamageLength : reachM;
}

/**
 * Whether this motion on this profile is a thrust. A `THRUST` motion is one
 * wherever the profile has a thrust corridor; `INSTANT` stays the instant
 * sector except on the profile whose stored skills spelled their thrust that
 * way (`legacyInstantMotion`).
 */
function isThrustMotion(profileId: WeaponContactProfileId, motion: SkillApplicationV2['motion']): boolean {
  const profile = BALANCE_THRUST_GEOMETRY_V0[profileId];
  return profile !== undefined && (motion.kind === 'THRUST' || (motion.kind === 'INSTANT' && profile.legacyInstantMotion === true));
}

/** Keep the displayed/lowered aperture identical to the runtime aperture. */
function scaledContactAngleDeg(baseAngleDeg: number, angleScale: number): number {
  return baseAngleDeg * angleScale;
}

/** The weapon's own unscaled aperture, from the ratified geometry tables. */
function baseContactAngleDeg(profileId: WeaponContactProfileId): number {
  if (profileId === 'ASSASSIN_BLADE' || profileId === 'GREATSWORD') return BALANCE_SPECIAL_GEOMETRY_V0[profileId].sectorDeg;
  const profile = weaponProfile(profileId);
  if (profile === undefined) return 0;
  if (profile.id === 'DAGGER_PAIR') return profile.leftSweepDeg + profile.rightSweepDeg - profile.overlapDeg;
  return 'sweepDeg' in profile ? profile.sweepDeg : 'closeSweepDeg' in profile ? profile.closeSweepDeg : 0;
}

/** The aperture a contact actually covers, after the request's angle scale. */
export function weaponContactArcDeg(profileId: WeaponContactProfileId, angleScale: number): number {
  return scaledContactAngleDeg(baseContactAngleDeg(profileId), angleScale);
}

/**
 * How fast a swung weapon's contact edge travels, in degrees per second.
 *
 * This is a sweep policy, and it had to be established rather than looked up.
 * `docs/gameplay/weapon-primary-basic-attack-contract.md` §16 states that V0
 * "does not yet split windup / active / recovery into three first-class
 * numeric runtime intervals"; `WEAPON_BASIC_ATTACK_TIMING_V0` owns only the
 * two-phase pre-hit/post-hit split and never an active duration; and
 * production never sets `sweepDurationS` at all. So there was no ratified
 * contact duration to borrow, and the first attempt — one whole attack cycle
 * — was wrong by a wide, measurable margin: a 120deg axe swing took 1.957s to
 * cross its own arc, during which an enemy walks 4.5m, well past the axe's
 * 2.75m reach.
 *
 * The rate is what the measurement picks, not a taste. A sweep must finish
 * inside the window the basic attack itself clamps a sweep to
 * (`Math.min(sweepDurationS, timing.postHitS)` in `authoritative-world-core`),
 * at the fastest cycle a character can reach — otherwise casting the skill
 * locks the player longer than swinging the same weapon does. Measured per
 * weapon by `scripts/weapon-sweep/sweep-duration-study.ts`, the minimum rate
 * that satisfies that is 98 deg/s for HEAVY_AXE, 128 for SWORD, 77 for
 * GREATSWORD and 633 for DAGGER_PAIR, whose 240deg two-hand arc and short
 * 0.379s window bind the constraint. 720 is the round rate above that bound.
 *
 * Expressing the policy as a RATE rather than a duration is what keeps it a
 * single number: each weapon's duration then comes from its own ratified
 * arc, so no per-weapon timing is invented here, and a player who asks for a
 * wider swing gets a proportionally longer one at the same blade speed
 * rather than a faster blade.
 */
export const WEAPON_SWEEP_RATE_DEG_PER_S = 720;

/** How long a swing of this aperture takes at the ratified blade speed. */
export function weaponSweepDurationS(profileId: WeaponContactProfileId, angleScale: number): number {
  return weaponContactArcDeg(profileId, angleScale) / WEAPON_SWEEP_RATE_DEG_PER_S;
}

/** Resolve an authored geometry independently of a live owner. */
export function resolveWeaponContactApplication(application: SkillApplicationV2): WeaponContactValidationResult {
  if (application.geometry.owner !== 'WEAPON_CONTACT') {
    return { ok: false, code: 'BAD_WEAPON_CONTACT_GEOMETRY', message: 'application geometry is not WEAPON_CONTACT', path: 'application.geometry' };
  }
  const geometry = application.geometry as WeaponGeometry;
  const profileId = geometry.profileId === undefined ? undefined : normalizeWeaponContactProfileId(geometry.profileId);
  if (geometry.profileId !== undefined && profileId === undefined) {
    return { ok: false, code: 'WEAPON_CONTACT_PROFILE_UNSUPPORTED', message: `no ratified weapon-contact profile matches "${geometry.profileId}"`, path: 'application.geometry.profileId' };
  }
  if (profileId === undefined) {
    return { ok: false, code: 'WEAPON_CONTACT_PROFILE_REQUIRED', message: 'weapon contact requires a concrete profileId for generation and runtime lowering', path: 'application.geometry.profileId' };
  }
  if (profileId === 'BOW_REFERENCE') {
    return { ok: false, code: 'WEAPON_CONTACT_PROJECTILE_PROFILE_UNSUPPORTED', message: 'BOW_REFERENCE is projectile contact and cannot be lowered as instantaneous weapon contact', path: 'application.geometry.profileId' };
  }
  const thrustProfile = BALANCE_THRUST_GEOMETRY_V0[profileId];
  if (application.motion.kind === 'THRUST' && thrustProfile === undefined) {
    return { ok: false, code: 'WEAPON_CONTACT_THRUST_UNSUPPORTED', message: `profile ${profileId} has no thrust corridor`, path: 'application.motion' };
  }
  const thrust = isThrustMotion(profileId, application.motion);
  const profile = weaponProfile(profileId);
  const special = profileId === 'ASSASSIN_BLADE' || profileId === 'GREATSWORD' ? BALANCE_SPECIAL_GEOMETRY_V0[profileId] : undefined;
  const reachM = thrust ? thrustProfile!.reachM : special?.reachM ?? (profile && 'effectiveReachM' in profile ? profile.effectiveReachM : undefined);
  const sweeping = application.motion.kind === 'WEAPON_SWEEP';
  const shapeWidthM = thrust ? thrustProfile!.widthM : sweeping || (profile && 'shape' in profile) ? bladeWidthM(profileId) : 0;
  // A whole-blade thrust has no sweep; its nominal aperture is the one its corridor subtends.
  const angleDeg = thrust && thrustProfile!.wholeBlade === true
    ? 2 * Math.atan(shapeWidthM / 2 / reachM!) * 180 / Math.PI
    : baseContactAngleDeg(profileId);
  if (!(reachM !== undefined && Number.isFinite(reachM) && reachM > 0)) {
    return { ok: false, code: 'WEAPON_CONTACT_PROFILE_UNSUPPORTED', message: `profile ${profileId} has no executable reach`, path: 'application.geometry.profileId' };
  }
  for (const axis of ['reach', 'width', 'angle'] as const) {
    const scale = geometry.scales[axis];
    if (!(Number.isFinite(scale) && scale > 0)) {
      return { ok: false, code: 'WEAPON_CONTACT_SCALE_UNSUPPORTED', message: `${axis} scale must be finite and positive for weapon contact`, path: `application.geometry.scales.${axis}` };
    }
  }
  // A width scale is a blade's width: a thrust corridor has one and every swing sweeps one.
  if (geometry.scales.width !== 1 && !thrust && !sweeping) {
    return { ok: false, code: 'WEAPON_CONTACT_WIDTH_SCALE_UNSUPPORTED', message: `profile ${profileId} has no ratified width scaling authority`, path: 'application.geometry.scales.width' };
  }
  const rootRegionId = geometry.regionId === undefined ? undefined : normalizeWeaponContactRegionId(profileId, geometry.regionId);
  if (geometry.regionId !== undefined && rootRegionId === undefined) {
    return { ok: false, code: 'WEAPON_CONTACT_REGION_UNSUPPORTED', message: `profile ${profileId} has no region "${geometry.regionId}"`, path: 'application.geometry.regionId' };
  }
  const bindings = geometry.contactBindings ?? [];
  // Binding effects are runtime-capable but are not yet walked by numeric
  // generation/appraisal. Executing them here would allow unpriced power.
  if (bindings.length > 0) {
    return { ok: false, code: 'WEAPON_CONTACT_BINDINGS_UNPRICED', message: 'contactBindings require generation valuation before they can execute', path: 'application.geometry.contactBindings' };
  }
  return { ok: true, value: {
    profileId,
    reachM: reachM * geometry.scales.reach,
    angleDeg: scaledContactAngleDeg(angleDeg, geometry.scales.angle),
    widthM: shapeWidthM * geometry.scales.width,
    ...(thrust ? { thrust: true as const } : {}),
    ...(thrust && profile !== undefined && 'closeSweepDeg' in profile
      ? { closeSweep: { reachM: reachM * geometry.scales.reach, angleDeg: scaledContactAngleDeg(profile.closeSweepDeg, geometry.scales.angle), withinM: profile.minThrustDistanceM } }
      : {}),
    ...(profileId === 'HEAVY_AXE' && sweeping ? {
      axeSweep: axeSweepGeometry(reachM * geometry.scales.reach,
        BALANCE_WEAPON_GEOMETRY_V0.HEAVY_AXE.shape.primaryDamageLength * geometry.scales.reach,
        shapeWidthM * geometry.scales.width, angleDeg * geometry.scales.angle * Math.PI / 180),
    } : sweeping && geometry.scales.width !== 1 ? {
      widthExtensionRad: sweptBladeHalfAngleRad(shapeWidthM * (geometry.scales.width - 1),
        Math.max(0, reachM * geometry.scales.reach - bladeLengthM(profileId, reachM) * geometry.scales.reach), reachM * geometry.scales.reach),
    } : {}),
    ...(rootRegionId === undefined ? {} : { rootRegionId }),
    application,
  } };
}

function sectorArea(reachM: number, angleDeg: number): number {
  return Math.PI * reachM * reachM * Math.max(0, Math.min(360, angleDeg)) / 360;
}

function regionAreaFraction(profileId: WeaponContactProfileId, resolved: WeaponContactResolvedApplication, regionId: string | undefined, enemyRadiusM: number): number {
  if (resolved.axeSweep) {
    const g = resolved.axeSweep;
    return regionId === undefined ? 1 : (regionId === 'head' ? g.headAreaM2 : g.shaftAreaM2) / g.areaM2;
  }
  if (profileId === 'DAGGER_PAIR') return regionId === undefined || regionId === 'blade' ? 1 : 0;
  if (profileId !== 'HEAVY_AXE' && profileId !== 'SPEAR') return regionId === undefined || regionId === 'edge' || regionId === 'blade' ? 1 : 0;
  const profile = BALANCE_WEAPON_GEOMETRY_V0[profileId];
  const geometry = resolved.application.geometry as WeaponGeometry;
  const primaryLength = profile.shape.primaryDamageLength * geometry.scales.reach;
  const headStart = Math.max(0, resolved.reachM - primaryLength);
  // The runtime puts a body in the primary region when `distance + radius >= headStart`, over centres out to
  // `reach + radius`: in centre space the region starts a radius sooner and the sector ends a radius later.
  const outerM = resolved.reachM + enemyRadiusM;
  const innerM = Math.max(0, headStart - enemyRadiusM);
  // A sector's area grows with the square of the distance; a thrust corridor's grows linearly along it.
  const primary = resolved.thrust === true
    ? Math.max(0, outerM - innerM) / Math.max(1e-9, outerM)
    : Math.max(0, outerM * outerM - innerM * innerM) / Math.max(1e-9, outerM * outerM);
  if (regionId === undefined) return 1;
  return regionId === (profileId === 'HEAVY_AXE' ? 'head' : 'tip') ? primary : 1 - primary;
}

/**
 * Analytic generation/appraisal projection of the same resolved profile.
 * Runtime contact remains the authority for individual hurtboxes; this only
 * supplies density-area and region-weighted expected values so a range/angle/
 * width request cannot be priced as a generic single-target cone.
 */
export function resolveWeaponContactEvaluation(
  application: SkillApplicationV2,
  /**
   * Representative enemy hitbox radius. The contact region is dilated by it,
   * because every runtime contact test admits an enemy whose BODY reaches the
   * region rather than one whose centre does — `inSector` compares against
   * `reachM + radius`, `inSegment` against `lengthM + radius` and
   * `widthM / 2 + radius`, and the axe sweep runs a true disk-vs-annular-sector
   * overlap (`diskTouchesAnnularSector`). Appraising the undilated region
   * under-priced every weapon sweep by that gap.
   */
  enemyRadiusM: number,
): WeaponContactEvaluationGeometry | undefined {
  const resolved = resolveWeaponContactApplication(application);
  if (!resolved.ok) return undefined;
  const { profileId, reachM, angleDeg, widthM } = resolved.value;
  const contactGeometry = application.geometry.owner === 'WEAPON_CONTACT' ? application.geometry : undefined;
  if (contactGeometry === undefined) return undefined;
  const regionId = resolved.value.rootRegionId;
  const baseProfile = weaponProfile(profileId);
  const sweptAngleDeg = baseProfile?.id === 'DAGGER_PAIR'
    ? scaledContactAngleDeg(baseProfile.leftSweepDeg + baseProfile.rightSweepDeg - baseProfile.overlapDeg, contactGeometry.scales.angle)
    : baseProfile !== undefined && 'sweepDeg' in baseProfile
      ? scaledContactAngleDeg(baseProfile.sweepDeg, contactGeometry.scales.angle)
      : angleDeg;
  // A widened blade reaches past both ends of the sweep.
  const effectiveAngleDeg = sweptAngleDeg + 2 * (resolved.value.widthExtensionRad ?? 0) * 180 / Math.PI;
  // Dilate by the enemy radius, following each runtime test:
  //   sector  — `inSector` admits `distance <= reachM + radius`, so the reach
  //             grows and the angle does not (the angular test stays on the
  //             enemy's centre).
  //   segment — `inSegment` admits `projectionM <= lengthM + radius` and
  //             `lateralM <= widthM / 2 + radius`, so the length grows once and
  //             the width grows on both sides.
  //   axe     — `diskTouchesAnnularSector` grows the annulus outward at its rim
  //             and inward at its hub. Modelled by scaling the resolved annular
  //             area by the same radial ratio the sector uses. APPROXIMATION:
  //             it captures the radial growth exactly and leaves the (smaller)
  //             angular widening at the sweep's ends unmodelled, so it is a
  //             mild under-estimate rather than an invented number.
  const dilatedReachM = reachM + enemyRadiusM;
  const sector = sectorArea(dilatedReachM, effectiveAngleDeg);
  // A thrust is priced by its corridor; the spear's close sweep
  // (target within `minThrustDistanceM`) is not the pricing geometry.
  // Model disjoint shaft-only and head-priority regions with the SAME scaled
  // head width and radial boundary used by the runtime. At radius zero these
  // reduce exactly to axeSweepGeometry().headAreaM2/shaftAreaM2; a nonzero
  // body can touch the head when its centre is within radius of innerM.
  const axeSweep = resolved.value.axeSweep;
  const shaftOnlyReachM = axeSweep === undefined ? 0 : Math.max(0, axeSweep.innerM - enemyRadiusM);
  const shaftAngleRad = axeSweep === undefined ? 0 : Math.min(2 * Math.PI, angleDeg * Math.PI / 180);
  const headAngleRad = axeSweep === undefined ? 0 : Math.min(2 * Math.PI, shaftAngleRad + 2 * axeSweep.bladeHalfAngleRad);
  const axeHeadAreaM2 = axeSweep === undefined ? 0 : (dilatedReachM ** 2 - shaftOnlyReachM ** 2) * headAngleRad / 2;
  const axeShaftAreaM2 = shaftOnlyReachM ** 2 * shaftAngleRad / 2;
  const areaM2 = axeSweep !== undefined
    ? axeHeadAreaM2 + axeShaftAreaM2
    : resolved.value.thrust === true
      ? (widthM + 2 * enemyRadiusM) * dilatedReachM
      : sector;
  const regionFraction = axeSweep !== undefined
    ? regionId === 'head' ? axeHeadAreaM2 / areaM2 : regionId === 'shaft' ? axeShaftAreaM2 / areaM2 : 1
    : regionAreaFraction(profileId, resolved.value, regionId, enemyRadiusM);
  let expectedPrimaryRegionMultiplier = 1;
  let expectedAdditionalRegionMultiplier = 1;
  // A dagger thrust is one corridor at full hit; only the sweep splits it into two half-damage hands.
  if (profileId === 'DAGGER_PAIR' && resolved.value.thrust !== true) {
    const dagger = BALANCE_WEAPON_GEOMETRY_V0.DAGGER_PAIR;
    expectedAdditionalRegionMultiplier = dagger.damagePerHandFraction *
      (dagger.leftSweepDeg + dagger.rightSweepDeg) / (dagger.leftSweepDeg + dagger.rightSweepDeg - dagger.overlapDeg);
  }
  else if (profileId === 'HEAVY_AXE') {
    // A broader blade adds full-damage head area, not just target count.
    // Use the resolved swept blade's width/reach/angle, never base-weapon
    // constants, so width-scaled skills are priced for their real coverage.
    const axe = BALANCE_WEAPON_GEOMETRY_V0.HEAVY_AXE;
    const headFraction = axeHeadAreaM2 / areaM2;
    expectedAdditionalRegionMultiplier = regionId === undefined ? headFraction + (1 - headFraction) * axe.shaftDamageFraction
      : regionId === 'head' ? 1 : axe.shaftDamageFraction;
    if (regionId !== undefined) expectedPrimaryRegionMultiplier = expectedAdditionalRegionMultiplier;
  } else if (profileId === 'SPEAR') {
    const spear = BALANCE_WEAPON_GEOMETRY_V0.SPEAR;
    if (resolved.value.thrust === true) {
      // The thrust is aimed at the nearest enemy, so nobody stands in front of
      // it: every extra body is behind the target, inside the tip zone. An
      // explicitly shaft-bound skill keeps its shaft fraction.
      expectedAdditionalRegionMultiplier = regionId === undefined || regionId === 'tip' ? 1 : spear.shaftDamageFraction;
    } else {
      // A sweep spreads its bodies over the whole sector, tip and shaft alike.
      const tipFraction = regionAreaFraction(profileId, resolved.value, 'tip', enemyRadiusM);
      expectedAdditionalRegionMultiplier = regionId === undefined ? tipFraction + (1 - tipFraction) * spear.shaftDamageFraction
        : regionId === 'tip' ? 1 : spear.shaftDamageFraction;
    }
    if (regionId !== undefined) expectedPrimaryRegionMultiplier = expectedAdditionalRegionMultiplier;
  }
  return {
    profileId,
    areaM2: areaM2 * regionFraction,
    ...(resolved.value.thrust === true ? { thrust: { reachM: dilatedReachM, halfWidthM: widthM / 2 + enemyRadiusM } } : {}),
    expectedPrimaryRegionMultiplier,
    expectedAdditionalRegionMultiplier,
  };
}

/** Resolve a concrete profile against the equipped source at activation. */
export function resolveWeaponContactForSource(application: SkillApplicationV2, source: WeaponContactSourceIdentity | undefined): WeaponContactValidationResult {
  const authored = resolveWeaponContactApplication(application);
  if (!authored.ok) return authored;
  if (source === undefined) return { ok: false, code: 'WEAPON_CONTACT_SOURCE_UNAVAILABLE', message: 'weapon contact requires an equipped source', path: 'player.equippedSource' };
  if (source.weaponId === undefined) return { ok: false, code: 'WEAPON_CONTACT_SOURCE_METADATA_REQUIRED', message: 'weapon contact requires equippedSource.weaponId metadata', path: 'player.equippedSource.weaponId' };
  const sourceProfile = normalizeWeaponContactProfileId(source.weaponId);
  if (sourceProfile === undefined) return { ok: false, code: 'WEAPON_CONTACT_SOURCE_PROFILE_UNSUPPORTED', message: `equipped source weaponId "${source.weaponId}" has no ratified contact profile`, path: 'player.equippedSource.weaponId' };
  if (sourceProfile !== authored.value.profileId) return { ok: false, code: 'WEAPON_CONTACT_SOURCE_PROFILE_MISMATCH', message: `equipped source profile ${sourceProfile} does not match skill profile ${authored.value.profileId}`, path: 'player.equippedSource.weaponId' };
  const expectedFamily = weaponContactProfileFamily(sourceProfile);
  if (source.family !== expectedFamily) return { ok: false, code: 'WEAPON_CONTACT_SOURCE_FAMILY_MISMATCH', message: `equipped source family ${source.family} does not match profile ${sourceProfile} family ${expectedFamily}`, path: 'player.equippedSource.family' };
  return authored;
}

export interface WeaponContactTargetResolution {
  readonly enemies: Enemy[];
  readonly contactRegions: ReadonlyMap<number, readonly WeaponContactHit[]>;
  readonly impact: Vec2;
  readonly attempted: boolean;
}

export interface WeaponContactTargetContext {
  readonly angularWindow?: { fromRad: number; toRad: number };
  readonly castOrigin: Vec2;
  readonly aimPoint: Vec2;
  readonly lockedTarget: Enemy | null;
  readonly source: WeaponContactSourceIdentity | undefined;
  /** Bodies this attack may still strike when it has already struck some (a sweep's earlier frames); absent means the policy's whole `maxPierce`. */
  readonly remainingPierce?: number;
}

function unit(from: Vec2, to: Vec2): Vec2 {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const d = Math.hypot(dx, dy);
  return d > 1e-9 ? { x: dx / d, y: dy / d } : { x: 1, y: 0 };
}

function inSector(origin: Vec2, enemy: Enemy, direction: Vec2, reachM: number, angleDeg: number): boolean {
  const relative = { x: enemy.pos.x - origin.x, y: enemy.pos.y - origin.y };
  const radius = resolveEnemyHitboxRadiusM(enemy.archetype);
  if (distance(origin, enemy.pos) > reachM + radius) return false;
  const angle = Math.atan2(relative.y, relative.x) - Math.atan2(direction.y, direction.x);
  let normalized = Math.abs(angle);
  if (normalized > Math.PI) normalized = 2 * Math.PI - normalized;
  return normalized <= (angleDeg * Math.PI) / 360;
}

function inSegment(origin: Vec2, enemy: Enemy, direction: Vec2, lengthM: number, widthM: number): { projectionM: number } | null {
  const relative = { x: enemy.pos.x - origin.x, y: enemy.pos.y - origin.y };
  const radius = resolveEnemyHitboxRadiusM(enemy.archetype);
  const projectionM = relative.x * direction.x + relative.y * direction.y;
  if (projectionM < 0 || projectionM > lengthM + radius) return null;
  const lateralM = Math.abs(relative.x * direction.y - relative.y * direction.x);
  if (lateralM > widthM / 2 + radius) return null;
  return { projectionM };
}

function region(profileId: WeaponContactProfileId, regionId: string, role: 'PRIMARY' | 'SECONDARY', fraction: number): WeaponContactHit {
  return { profileId, regionId, role, fraction };
}

function candidateEnemies(state: Pick<SimState, 'enemies' | 'terrain'>, origin: Vec2): Enemy[] {
  return state.enemies.filter((enemy) => enemy.alive && lineOfEffect(state.terrain, origin, enemy.pos, 'areaEffect'));
}

function normalizedAngleDeg(value: number): number {
  let angle = value % 360;
  if (angle > 180) angle -= 360;
  if (angle < -180) angle += 360;
  return angle;
}

function selected(regions: readonly WeaponContactHit[], rootRegionId: string | undefined): readonly WeaponContactHit[] {
  return rootRegionId === undefined ? regions : regions.filter((entry) => entry.regionId === rootRegionId);
}

function limited(enemies: Enemy[], cap: number | undefined, origin: Vec2): Enemy[] {
  const ordered = [...enemies].sort((a, b) => distance(origin, a.pos) - distance(origin, b.pos) || a.id - b.id);
  return cap === undefined ? ordered : ordered.slice(0, cap);
}

function toBasicContactCoordinates(origin: Vec2, direction: Vec2, point: Vec2): { x: number; y: number } {
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  // The basic resolver's canonical forward vector is +X. Rotate the live
  // cast frame into that coordinate system so its scale-1 result is exactly
  // the same contact set as an ordinary weapon swing at this origin/facing.
  return { x: dx * direction.x + dy * direction.y, y: -dx * direction.y + dy * direction.x };
}

function scaleIsIdentity(application: SkillApplicationV2): boolean {
  return application.geometry.owner === 'WEAPON_CONTACT'
    && application.geometry.scales.reach === 1
    && application.geometry.scales.width === 1
    && application.geometry.scales.angle === 1;
}

function scaleOneContactFractions(
  profileId: BalanceWeaponId,
  candidates: readonly Enemy[],
  origin: Vec2,
  direction: Vec2,
): ReadonlyMap<number, number> {
  const basicEnemies = candidates.map((enemy) => {
    const local = toBasicContactCoordinates(origin, direction, enemy.pos);
    return {
      id: enemy.id,
      x: local.x,
      y: local.y,
      hp: enemy.hp,
      maxHp: enemy.maxHp,
      active: enemy.alive,
      resolution: 'ACTIVE' as const,
      radiusM: resolveEnemyHitboxRadiusM(enemy.archetype),
    };
  });
  return resolveBalanceAttack(profileId, basicEnemies, 1, 0).damageByEnemyId;
}

/** Resolve one instantaneous weapon-contact release against live hurtboxes. */
export function resolveWeaponContactTargets(state: Pick<SimState, 'enemies' | 'terrain'>, application: SkillApplicationV2, context: WeaponContactTargetContext): WeaponContactTargetResolution {
  const resolved = resolveWeaponContactForSource(application, context.source);
  if (!resolved.ok) return { enemies: [], contactRegions: new Map(), impact: context.aimPoint, attempted: false };
  const { profileId, reachM, angleDeg, widthM, rootRegionId } = resolved.value;
  const contactGeometry = application.geometry.owner === 'WEAPON_CONTACT' ? application.geometry : undefined;
  if (contactGeometry === undefined) return { enemies: [], contactRegions: new Map(), impact: context.aimPoint, attempted: false };
  const extensionDeg = (resolved.value.widthExtensionRad ?? 0) * 180 / Math.PI;
  const reachScale = contactGeometry.scales.reach;
  const angleScale = contactGeometry.scales.angle;
  const direction = unit(context.castOrigin, context.aimPoint);
  // A weapon contact strikes whatever its area reaches unless its contact
  // policy caps the bodies it may strike (`maxPierce`, the same count a
  // projectile's is), nearest first; `src/eval/targeting.ts` prices both by the
  // area against the density, capped the same way.
  const cap = context.remainingPierce ?? application.contactPolicy.maxPierce;
  const hits = new Map<number, readonly WeaponContactHit[]>();
  const struck: Enemy[] = [];
  const add = (enemy: Enemy, entries: readonly WeaponContactHit[]) => {
    const filtered = selected(entries, rootRegionId);
    if (filtered.length === 0) return;
    hits.set(enemy.id, filtered);
    struck.push(enemy);
  };
  const candidates = candidateEnemies(state, context.castOrigin);
  if (resolved.value.axeSweep) {
    const g = resolved.value.axeSweep;
    const half = angleDeg * Math.PI / 360;
    const window = context.angularWindow ?? { fromRad: -half, toRad: half };
    for (const enemy of candidates) {
      const local = toBasicContactCoordinates(context.castOrigin, direction, enemy.pos);
      const radius = resolveEnemyHitboxRadiusM(enemy.archetype);
      const head = diskTouchesAnnularSector(local, radius, g.innerM, reachM,
        window.fromRad - g.bladeHalfAngleRad, window.toRad + g.bladeHalfAngleRad);
      const shaft = diskTouchesAnnularSector(local, radius, 0, g.innerM, window.fromRad, window.toRad);
      if (head || shaft) add(enemy, [region(profileId, head ? 'head' : 'shaft', head ? 'PRIMARY' : 'SECONDARY',
        head ? 1 : BALANCE_WEAPON_GEOMETRY_V0.HEAVY_AXE.shaftDamageFraction)]);
    }
    const kept = limited(struck, cap, context.castOrigin);
    const ids = new Set(kept.map(enemy => enemy.id));
    for (const id of hits.keys()) if (!ids.has(id)) hits.delete(id);
    return { enemies: kept, contactRegions: hits, impact: context.aimPoint, attempted: true };
  }
  const thrustProfile = BALANCE_THRUST_GEOMETRY_V0[profileId];
  if (resolved.value.thrust === true && thrustProfile?.wholeBlade === true) {
    // A whole-blade thrust: one straight corridor along the aim, all blade.
    // Same segment test (and enemy-radius dilation) as the spear's far thrust.
    const regionName = profileId === 'SWORD' ? 'edge' : 'blade';
    for (const enemy of candidates) {
      if (inSegment(context.castOrigin, enemy, direction, reachM, widthM)) add(enemy, [region(profileId, regionName, 'PRIMARY', 1)]);
    }
    const kept = limited(struck, cap, context.castOrigin);
    const keep = new Set(kept.map((enemy) => enemy.id));
    for (const id of [...hits.keys()]) if (!keep.has(id)) hits.delete(id);
    return { enemies: kept, contactRegions: hits, impact: context.aimPoint, attempted: true };
  }
  // A spear sweep is the close sweep alone (the basic resolver picks sweep or
  // thrust by the target's distance), and a catalyst has no basic attack.
  const sweptSpear = profileId === 'SPEAR' && application.motion.kind === 'WEAPON_SWEEP';
  if (scaleIsIdentity(application) && profileId !== 'CATALYST' && !sweptSpear) {
    // Scale-1 is a strict parity bridge to the existing basic-attack spatial
    // authority.  Scaled forms use the explicit branches below, but they
    // retain the same profile formulas and region fractions while extending
    // reach/width/angle deliberately.
    const basicFractions = scaleOneContactFractions(profileId, candidates, context.castOrigin, direction);
    for (const enemy of candidates) {
      const fraction = basicFractions.get(enemy.id);
      if (fraction === undefined) continue;
      let entries: WeaponContactHit[];
      if (profileId === 'DAGGER_PAIR') {
        const local = toBasicContactCoordinates(context.castOrigin, direction, enemy.pos);
        const rawAngle = normalizedAngleDeg(Math.atan2(local.y, local.x) * 180 / Math.PI);
        const angle = Math.abs(rawAngle) < 1e-9 ? 0 : rawAngle;
        const profile = BALANCE_WEAPON_GEOMETRY_V0.DAGGER_PAIR;
        entries = [];
        if (angle >= -profile.leftSweepDeg + profile.overlapDeg / 2 && angle <= profile.overlapDeg / 2) entries.push(region(profileId, 'blade', 'PRIMARY', profile.damagePerHandFraction));
        if (angle <= profile.rightSweepDeg - profile.overlapDeg / 2 && angle >= -profile.overlapDeg / 2) entries.push(region(profileId, 'blade', 'PRIMARY', profile.damagePerHandFraction));
      } else if (profileId === 'HEAVY_AXE') {
        const profile = BALANCE_WEAPON_GEOMETRY_V0.HEAVY_AXE;
        const headStart = profile.effectiveReachM - profile.shape.primaryDamageLength;
        entries = [distance(context.castOrigin, enemy.pos) + resolveEnemyHitboxRadiusM(enemy.archetype) >= headStart
          ? region(profileId, 'head', 'PRIMARY', 1)
          : region(profileId, 'shaft', 'SECONDARY', profile.shaftDamageFraction)];
      } else if (profileId === 'SPEAR') {
        const profile = BALANCE_WEAPON_GEOMETRY_V0.SPEAR;
        const targetDistance = context.lockedTarget ? distance(context.castOrigin, context.lockedTarget.pos) : distance(context.castOrigin, context.aimPoint);
        const tipStart = targetDistance <= profile.minThrustDistanceM
          ? profile.effectiveReachM - profile.shape.primaryDamageLength
          : Math.max(0, Math.min(targetDistance, profile.effectiveReachM) - profile.shape.primaryDamageLength);
        const local = toBasicContactCoordinates(context.castOrigin, direction, enemy.pos);
        const contactProjection = local.x;
        entries = [contactProjection + resolveEnemyHitboxRadiusM(enemy.archetype) >= tipStart
          ? region(profileId, 'tip', 'PRIMARY', 1)
          : region(profileId, 'shaft', 'SECONDARY', profile.shaftDamageFraction)];
      } else {
        entries = [region(profileId, profileId === 'SWORD' ? 'edge' : 'blade', 'PRIMARY', fraction)];
      }
      // Dagger's two half-swings are already represented as two entries; for
      // all other profiles the resolver's fraction is authoritative. The
      // latter matters for the shaft branch, where damage is intentionally
      // less than one whole basic hit.
      if (profileId !== 'DAGGER_PAIR' && entries.length > 0 && entries[0]!.fraction !== fraction) {
        entries = [{ ...entries[0]!, fraction }];
      }
      add(enemy, entries);
    }
    const kept = limited(struck, cap, context.castOrigin);
    const keep = new Set(kept.map((enemy) => enemy.id));
    for (const id of [...hits.keys()]) if (!keep.has(id)) hits.delete(id);
    return { enemies: kept, contactRegions: hits, impact: context.aimPoint, attempted: true };
  }
  if (profileId === 'DAGGER_PAIR') {
    const profile = BALANCE_WEAPON_GEOMETRY_V0.DAGGER_PAIR;
    // A widened blade reaches past the outer end of each hand's sweep.
    const leftAngle = scaledContactAngleDeg(profile.leftSweepDeg, angleScale) + extensionDeg;
    const rightAngle = scaledContactAngleDeg(profile.rightSweepDeg, angleScale) + extensionDeg;
    const overlapAngle = scaledContactAngleDeg(profile.overlapDeg, angleScale);
    for (const enemy of candidates) {
      const relative = { x: enemy.pos.x - context.castOrigin.x, y: enemy.pos.y - context.castOrigin.y };
      const rawAngle = normalizedAngleDeg((Math.atan2(relative.y, relative.x) - Math.atan2(direction.y, direction.x)) * 180 / Math.PI);
      const angle = Math.abs(rawAngle) < 1e-9 ? 0 : rawAngle;
      if (Math.hypot(relative.x, relative.y) <= reachM + resolveEnemyHitboxRadiusM(enemy.archetype)
        && ((angle >= -leftAngle + overlapAngle / 2 && angle <= overlapAngle / 2)
          || (angle <= rightAngle - overlapAngle / 2 && angle >= -overlapAngle / 2))) {
        const entries: WeaponContactHit[] = [];
        if (angle >= -leftAngle + overlapAngle / 2 && angle <= overlapAngle / 2) entries.push(region(profileId, 'blade', 'PRIMARY', profile.damagePerHandFraction));
        if (angle <= rightAngle - overlapAngle / 2 && angle >= -overlapAngle / 2) entries.push(region(profileId, 'blade', 'PRIMARY', profile.damagePerHandFraction));
        add(enemy, entries);
      }
    }
  } else if (profileId === 'HEAVY_AXE') {
    const profile = BALANCE_WEAPON_GEOMETRY_V0.HEAVY_AXE;
    const headLength = profile.shape.primaryDamageLength * reachScale;
    for (const enemy of candidates) if (inSector(context.castOrigin, enemy, direction, reachM,
      scaledContactAngleDeg(profile.sweepDeg, angleScale))) {
      const start = reachM - headLength;
      add(enemy, [distance(context.castOrigin, enemy.pos) + resolveEnemyHitboxRadiusM(enemy.archetype) >= start
        ? region(profileId, 'head', 'PRIMARY', 1)
        : region(profileId, 'shaft', 'SECONDARY', profile.shaftDamageFraction)]);
    }
  } else if (profileId === 'SPEAR') {
    const profile = BALANCE_WEAPON_GEOMETRY_V0.SPEAR;
    const targetDistance = context.lockedTarget ? distance(context.castOrigin, context.lockedTarget.pos) : distance(context.castOrigin, context.aimPoint);
    const close = sweptSpear || targetDistance <= profile.minThrustDistanceM;
    if (close) {
      const tipLength = profile.shape.primaryDamageLength * reachScale;
      for (const enemy of candidates) if (inSector(context.castOrigin, enemy, direction, reachM,
        scaledContactAngleDeg(profile.closeSweepDeg, angleScale) + 2 * extensionDeg)) {
        const start = reachM - tipLength;
        add(enemy, [distance(context.castOrigin, enemy.pos) + resolveEnemyHitboxRadiusM(enemy.archetype) >= start
          ? region(profileId, 'tip', 'PRIMARY', 1)
          : region(profileId, 'shaft', 'SECONDARY', profile.shaftDamageFraction)]);
      }
    } else {
      // Full-reach thrust, same rule as the basic resolver: the segment runs the
      // whole scaled reach, and the tip zone runs from a tip's length short of
      // the target to the end — target and everything behind it take the tip.
      const extension = reachM;
      const tipLength = profile.shape.primaryDamageLength * reachScale;
      const start = Math.max(0, Math.min(targetDistance, reachM) - tipLength);
      for (const enemy of candidates) {
        const contact = inSegment(context.castOrigin, enemy, direction, extension, widthM);
        if (!contact) continue;
        add(enemy, [contact.projectionM + resolveEnemyHitboxRadiusM(enemy.archetype) >= start
          ? region(profileId, 'tip', 'PRIMARY', 1)
          : region(profileId, 'shaft', 'SECONDARY', profile.shaftDamageFraction)]);
      }
    }
  } else {
    const profile = weaponProfile(profileId);
    const profileAngle = profileId === 'ASSASSIN_BLADE' || profileId === 'GREATSWORD'
      ? BALANCE_SPECIAL_GEOMETRY_V0[profileId].sectorDeg
      : profile && 'sweepDeg' in profile ? profile.sweepDeg : 0;
    for (const enemy of candidates) if (inSector(context.castOrigin, enemy, direction, reachM, profileAngle * angleScale + 2 * extensionDeg)) {
      add(enemy, [region(profileId, profileId === 'SWORD' ? 'edge' : 'blade', 'PRIMARY', 1)]);
    }
    const kept = limited(struck, cap, context.castOrigin);
    const keep = new Set(kept.map((enemy) => enemy.id));
    for (const id of [...hits.keys()]) if (!keep.has(id)) hits.delete(id);
    return { enemies: kept, contactRegions: hits, impact: context.aimPoint, attempted: true };
  }
  const kept = limited(struck, cap, context.castOrigin);
  const keep = new Set(kept.map((enemy) => enemy.id));
  for (const id of [...hits.keys()]) if (!keep.has(id)) hits.delete(id);
  return { enemies: kept, contactRegions: hits, impact: context.aimPoint, attempted: true };
}

export function bindingMatchesContact(binding: NonNullable<WeaponGeometry['contactBindings']>[number], hit: WeaponContactHit): boolean {
  if (binding.selector.kind === 'ROLE') return binding.selector.role === hit.role;
  const profileId = normalizeWeaponContactProfileId(binding.selector.profileId);
  const regionId = profileId === undefined ? undefined : normalizeWeaponContactRegionId(profileId, binding.selector.regionId);
  return profileId === hit.profileId && regionId === hit.regionId;
}
